import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Fade,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { 
  AdminPanelSettings as AdminPanelSettingsIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useBackgroundImage } from '../hooks/useBackgroundImage';
import { BackgroundSelector } from '../components/BackgroundSelector';
import { validatePassword } from '../utils/passwordValidator';
import { PasswordRequirements } from '../components/PasswordRequirements';

export const CreateFirstAdmin: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(validatePassword(''));
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { currentBg, backgroundImages, selectedBackground, changeBackground, fallbackGradient, useFallback } = useBackgroundImage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      showError(t('auth.passwordMismatch'));
      return;
    }

    const validation = validatePassword(password);
    if (!validation.isValid) {
      showError(validation.errors.join('; '));
      return;
    }

    setLoading(true);

    try {
      // Obtener token de setup primero
      const { setup_token } = await apiService.getSetupToken();
      
      // Crear admin con el token
      await apiService.createFirstAdmin({
        email,
        password,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
      }, setup_token);
      showSuccess(t('auth.firstAdminCreated'));
      // Redirigir inmediatamente - FirstAdminGuard detectará el cambio
      navigate('/login');
    } catch (err: any) {
      showError(err.response?.data?.detail || t('auth.createFirstAdminError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        backgroundImage: useFallback 
          ? fallbackGradient 
          : `url(${currentBg.url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: useFallback ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.4)',
          zIndex: 0,
        },
      }}
    >
      {/* Selector de fondo */}
      <BackgroundSelector
        selectedBackground={selectedBackground}
        backgroundImages={backgroundImages}
        onBackgroundChange={changeBackground}
        fallbackGradients={{
          '1': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '2': 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          '3': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        }}
      />

      {/* Formulario de registro de administrador */}
      <Fade in={true}>
        <Paper
          elevation={24}
          sx={{
            position: 'relative',
            zIndex: 1,
            p: 3,
            width: '100%',
            maxWidth: 540,
            borderRadius: 4,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 38.4, color: 'primary.main', mb: 1 }} />
            <Typography
              component="h1"
              variant="h3"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.5px',
                mb: 0.5,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('auth.createFirstAdmin')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {t('auth.createFirstAdminSubtitle')}
            </Typography>
            <Alert severity="info" sx={{ mt: 1, textAlign: 'left' }}>
              {t('auth.createFirstAdminInfo')}
            </Alert>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('common.email')}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
              <TextField
                margin="normal"
                fullWidth
                id="firstName"
                label={t('common.firstName')}
                name="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                margin="normal"
                fullWidth
                id="lastName"
                label={t('common.lastName')}
                name="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('common.password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordValidation(validatePassword(e.target.value));
              }}
              error={password.length > 0 && !passwordValidation.isValid}
              helperText={
                password.length > 0 && !passwordValidation.isValid
                  ? passwordValidation.errors[0]
                  : t('auth.passwordMinLengthHelper')
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: password.length > 0 ? 0.5 : 1.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            {password.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <PasswordRequirements validation={passwordValidation} />
              </Box>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t('common.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && password !== confirmPassword
                  ? t('auth.passwordMismatch')
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 1.5,
                mb: 1.5,
                py: 1.2,
                borderRadius: 2,
                fontSize: '0.8rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.createAdmin')}
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

